import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter, ModalHeader, Row, Col, Form, FormGroup, Input, ListGroup, ListGroupItem, UncontrolledTooltip } from 'reactstrap';
import React, { useEffect, useState } from "react";
import { getPrimaryButtonClass, getPrimaryTextClass } from "helpers/sf_utils";
import { useDispatch, useSelector } from 'react-redux';
import DeleteModal from '../DeleteModal';
import { NOTE_DELETE_STR } from 'common/sf_strings';
import { deleteLeadNotes, updateLeadNotes, deleteCallNotes, updateCallNotes } from 'store/notes/lead_notes/actions';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import orderBy from 'lodash/orderBy';
import useNotesModalScroll from 'hooks/useNotesModalScroll';
import { getNotesGroupByLeadId, getNotesGroupByCallId } from 'helpers/sf_lead_notes_helper';
import { bsh_getClientUserDetails, bsh_isAdminUserDetailsPresent } from 'helpers/sf_browser_storage_helper';
import { printNameOrEmail } from "../../../helpers/sf_utils";

const LeadNoteModal = ({ modalState, onSubmitClick, onCloseClick, textHeading }) => {

  const dispatch = useDispatch();

  const { lead_notes, call_notes, current_account, current_admin_user } = useSelector((state) => {
    return {
      lead_notes: state.leadNotesReducer.lead_notes,
      call_notes: state.leadNotesReducer.call_notes,
      current_account: state.accounts.current_account,
      current_user: state.Login.user,
      current_admin_user: state.LoginAdminUser.current_user,
    };
  });

  const current_client = bsh_getClientUserDetails();
  
  const { showTooltip, handleScroll } = useNotesModalScroll()

  // Determine if this is for call notes or lead notes
  const isCallNotes = modalState?.call_id !== null && modalState?.call_id !== undefined;
  
  const [inputText, setInputText] = useState('');
  const [notes, setNotes] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [deleteNoteModal, setDeleteNoteModal] = useState({
    action: false,
    note: {},
  })

  const sortedNotes = orderBy(notes, ['created_at'], ['desc']);

  useEffect(() => {
    if (isCallNotes) {
      setNotes(getNotesGroupByCallId(call_notes, modalState?.call_id) || []);
    } else {
      setNotes(getNotesGroupByLeadId(lead_notes, modalState?.lead_id) || []);
    }
  }, [lead_notes, call_notes, modalState, isCallNotes]);

  useEffect(() => {
    if (modalState?.notes) {
      setNotes(modalState.notes);
    }
  }, [modalState]);

  
  const canEditOrDelete = (note) => {
    const isAdminViewingClientData = bsh_isAdminUserDetailsPresent() && !!current_client?.token;

    if (isAdminViewingClientData) {
      return true;
    }

    if (note?.admin_user_id) {
      return false;
    }
    
    return true;
  };

const renderUserNameAndAdminStatus = (note) => {
  let userName = "loading...";
  let isAdmin = false;

  if (note?.admin_user) {
    userName = printNameOrEmail(note.admin_user.first_name, note.admin_user.last_name);
    isAdmin = true;
  } else if (note?.user) {
    userName = printNameOrEmail(note.user.first_name, note.user.last_name);
  }

  return (
    <>
      {userName}
      {isAdmin && (
        <span style={{ color: "red" }}>{" "}(Admin)</span>
      )}
    </>
  );
};

  const onClickSubmitModalState = () => {
    if (inputText.trim() !== '') {
      const currentLeadId = modalState?.lead_id;
      const currentCallId = modalState?.call_id;

      if (editingIndex !== -1) {
        // Update existing note
        const noteToUpdate = notes[editingIndex];
        setNotes((prevNotes) => {
          const updatedNotes = [...prevNotes];
          if (isCallNotes) {
            updatedNotes[editingIndex] = { text: inputText, quote_call_id: currentCallId };
          } else {
            updatedNotes[editingIndex] = { text: inputText, lead_id: currentLeadId };
          }
          return updatedNotes;
        });
        setEditingIndex(-1); // Reset editing index after updating
        
        if (isCallNotes) {
          dispatch(updateCallNotes({ 
            id: noteToUpdate?.id,
            call_note: {
              text: inputText
            }
          }));
        } else {
          dispatch(updateLeadNotes({ 
            id: noteToUpdate?.id,
            lead_id: currentLeadId,
            lead_note: {
              text: inputText
            }
          }));
        }
      } else {
        // Add new note
        if (isCallNotes) {
          setNotes([...notes, { text: inputText, quote_call_id: currentCallId }]);
        } else {
          setNotes([...notes, { text: inputText, lead_id: currentLeadId }]);
        }
        const newNote = {
          text: inputText,
          lead_id: currentLeadId,
        };
        if (isCallNotes) {
          newNote.call_id = currentCallId;
        }
        // Prioritize admin user, then regular user, then client
        if (current_admin_user) {
          newNote.admin_user_id = current_admin_user.id;
        }
        else if (current_client) {
          newNote.client_id = current_client.id;
        }
        onSubmitClick(newNote);
      }
      setInputText('');
    }
  };

  const onClickRemoveNote = (note_obj) => {
    const noteId = note_obj?.note?.id;
    const currentLeadId = modalState?.lead_id;
    setNotes((prevNotes) => prevNotes.filter((note, i) => note.id !== noteId));
    if (isCallNotes) {
      dispatch(deleteCallNotes({ id: noteId }));
    } else {
      dispatch(deleteLeadNotes({ id: noteId, lead_id: currentLeadId }));
    }
    setDeleteNoteModal({ action: false, note: {} })
  };

  const onClickEditNote = (index) => {
    // Find the note in the original notes array by matching the sorted note's ID
    const sortedNote = sortedNotes[index];
    const originalIndex = notes.findIndex(note => note.id === sortedNote?.id);
    if (canEditOrDelete(sortedNote)) {
      setEditingIndex(originalIndex);
      setInputText(sortedNote?.text);
    }
  };

  const handleCloseModal = () => {
    setEditingIndex(-1);
    setInputText('');
    onCloseClick();
  };

  return (
    <>
      <DeleteModal
        deleteModalState={deleteNoteModal}
        onDeleteClick={onClickRemoveNote}
        textMessage={NOTE_DELETE_STR}
        onCloseClick={() => setDeleteNoteModal({ action: false, note: {} })}
        cancelButton
      />
      <Modal isOpen={modalState.action} toggle={handleCloseModal} size={'lg'} centered={true} className='sf-pro-font'>
        <ModalHeader>
          <div className="d-flex justify-content-center">
            <i className={`align-self-bottom label-title bx bx-notepad ${getPrimaryTextClass()} me-1`}></i>
            <span className="modal-title call-transcription-topic-heading">
              {textHeading}
            </span>
          </div>
          <button
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={handleCloseModal}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12} className="mx-auto">
              <ListGroup className="my-3" style={{ maxHeight: "290px", overflowY: "scroll", overflowX: "hidden" }} onScroll={handleScroll}>
                {sortedNotes?.map((note, index) => (
                  <ListGroupItem className='mb-3 border-top' key={index} style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                    <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '100%' }}>
                      {note?.text}
                    </div>
                    <div className="mt-2 d-flex justify-content-between border-top" style={{ flexWrap: 'wrap' }}>
                      <div className="mt-2" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                        <span className='mx-2'>
                          <i className={`bx bx-user ${getPrimaryTextClass()} me-2`}></i>
                          {renderUserNameAndAdminStatus(note)}
                        </span>
                        <span className='mx-2'>
                          <i className={`bx bx-calendar ${getPrimaryTextClass()} me-2`}></i>
                          {note?.created_at ? format(new Date(utcToZonedTime(note?.created_at, current_account?.timezone)), `MM-dd-yyyy hh:mm a`,) : ''}
                        </span>
                      </div>

                      {canEditOrDelete(note) && (
                        <div className="mt-2">
                          <span className='mx-2'>
                            <i
                              id={`tooltip-lead-delete-${index}`}
                              className={`mdi mdi-delete font-size-16 ${getPrimaryTextClass()} me-2`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setDeleteNoteModal({ action: true, note: { id: note.id } })}
                            ></i>
                            {showTooltip && <UncontrolledTooltip placement="top" target={`tooltip-lead-delete-${index}`}>
                              Delete
                            </UncontrolledTooltip>}
                          </span>
                          <span className='mx-2'>
                            <i
                              id={`tooltip-lead-edit-${index}`}
                              className={`bx bx-edit font-size-14 ${getPrimaryTextClass()} me-2`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => onClickEditNote(index)}
                            ></i>
                            {showTooltip && <UncontrolledTooltip placement="top" target={`tooltip-lead-edit-${index}`}>
                              Edit
                            </UncontrolledTooltip>}
                          </span>
                        </div>
                      )}
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
              <Form>
                <FormGroup>
                  <Input
                    type="textarea"
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    placeholder="Type to add new note"
                  />
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <button type="button" className={`btn ${getPrimaryButtonClass()} ms-2`} onClick={onClickSubmitModalState}>{editingIndex !== -1 ? 'Update' : 'Add'}</button>
          <button type="button" className="btn btn-light ms-2" onClick={handleCloseModal}>Cancel</button>
        </ModalFooter>
      </Modal>
    </>
  );
};

LeadNoteModal.propTypes = {
  onCloseClick: PropTypes.func,
  onSubmitClick: PropTypes.func,
  modalState: PropTypes.any,
  textHeading: PropTypes.any,
  cancelButton: PropTypes.bool
};

export default LeadNoteModal;